import { useEffect, useState } from 'react';
import { Loader2, RotateCcw, Save, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAdminAiSettings, useSaveAiPrompt, useResetAiPrompt } from '@/api/hooks';
import type { AiPromptId } from '@/api/client';
import { cn } from '@/lib/utils';

const TABS: { id: AiPromptId; label: string; blurb: string }[] = [
  { id: 'ticket', label: 'Engineering AI', blurb: 'Sent with every ticket-assistant request on the ticket detail page.' },
  { id: 'sales',  label: 'Sales AI',       blurb: 'Sent with every sales-assistant request — opportunity detail and the Open/My Opportunities pipeline chat.' },
];

export default function AdminAiAssistantPage() {
  const { data, isLoading, error } = useAdminAiSettings();
  const save = useSaveAiPrompt();
  const reset = useResetAiPrompt();
  const [tab, setTab] = useState<AiPromptId>('ticket');
  const [draft, setDraft] = useState('');

  const active = data?.prompts?.[tab];

  // Sync the editor when settings load, the tab changes, or a save/reset refetches.
  useEffect(() => {
    if (active) setDraft(active.systemPrompt);
  }, [active]);

  const dirty = active != null && draft.trim() !== active.systemPrompt.trim();
  const busy = save.isPending || reset.isPending;

  const onSave = () => {
    if (!draft.trim()) { toast.error('The prompt can’t be empty.'); return; }
    save.mutate({ prompt: tab, systemPrompt: draft.trim() }, {
      onSuccess: () => toast.success('System prompt saved — applies to new chats.'),
      onError: (e) => toast.error('Save failed', { description: e instanceof Error ? e.message : 'Unknown error' }),
    });
  };

  const onReset = () => {
    reset.mutate(tab, {
      onSuccess: () => toast.success('Reset to the built-in default.'),
      onError: (e) => toast.error('Reset failed', { description: e instanceof Error ? e.message : 'Unknown error' }),
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold">AI Assistant</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Each assistant has its own system prompt. Changes apply immediately to new chats.
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load AI settings'}
        </div>
      )}

      {data && active && (
        <>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <span className="text-muted-foreground">Model: <span className="font-mono text-foreground">{data.model}</span></span>
            <span className="flex items-center gap-1.5">
              {data.configured
                ? <><CheckCircle2 className="h-3.5 w-3.5 text-success" /><span className="text-success font-medium">Foundry configured</span></>
                : <><XCircle className="h-3.5 w-3.5 text-destructive" /><span className="text-destructive font-medium">Foundry not configured</span></>}
            </span>
          </div>

          {/* Prompt switcher */}
          <div className="flex gap-1 border-b border-border">
            {TABS.map((t) => {
              const state = data.prompts[t.id];
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={cn(
                    '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                    tab === t.id
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t.label}
                  {state?.isCustom && (
                    <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      custom
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <span className="text-muted-foreground">{TABS.find((t) => t.id === tab)?.blurb}</span>
            <span className="text-muted-foreground">
              {active.isCustom
                ? `Custom prompt${active.updatedAt ? ` · updated ${new Date(active.updatedAt).toLocaleString()}` : ''}`
                : 'Using built-in default'}
            </span>
          </div>

          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck
            className="h-[420px] w-full resize-y rounded-lg border border-input bg-background p-3 font-mono text-[13px] leading-relaxed text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={onSave} disabled={!dirty || busy}>
              {save.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
              Save
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onReset}
              disabled={!active.isCustom || busy}
              title={active.isCustom ? 'Remove the custom prompt and use the built-in default' : 'Already using the default'}
            >
              {reset.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />}
              Reset to default
            </Button>
            {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
          </div>
        </>
      )}
    </div>
  );
}
